import '../styles/ResearchPage.css';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../authentication/AuthContext';
import { Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions, Collapse } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import ResearchDescription from './ResearchDescription'; // Import the new component
import Step1Survey from './SurveyStep/Step1Survey.js';
import Step2Personas from './PersonasStep/Step2Personas';
import Step3Responses from './ResponsesStep/Step3Responses';
import Step4FinalReview from './FinalStep/Step4FinalReview.js';
import { api } from '../services/api';
import { mapServerResponsesToLocal, mapServerQuestionsToLocal } from '../services/apiUtils';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../styles/Theme.js';

function ResearchPage() {
  const { sub } = useAuth();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [topicPendingDeletion, setTopicPendingDeletion] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Set initial loading state to true
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);
  const [showDescription, setShowDescription] = useState(false);

  // Research workflow state
  const [userData, setUserData] = useState({
    userID: sub,
    researchID: '',
    description: '',
    surveyDescription: '',
    title: '',
    surveyQuestions: null,
    selectedPersonas: [],
    personaFilters: null,
    surveyResponses: null,
    surveyContext: '',
    lastGenerationState: null, // Added to track last generation state
  });

  /**
   * Converts a UNIX timestamp (seconds) to a JavaScript Date object.
   */
  const toDate = (unixSeconds) => {
    return new Date(unixSeconds * 1000);
  };

  /**
   * Returns an array of sections, each with a label and an array of topics.
   * The sections are ordered: Today -> Yesterday -> Prev 7d -> Prev 30d -> older months (descending).
   * Topics are placed in the *most recent* category they qualify for.
   */
  const groupTopicsBySections = (topicsList) => {
    // Sort descending by lastModified
    const sorted = [...topicsList].sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0));

    // Prepare cutoffs
    const now = new Date();
    // midnight of current day
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    // 7 days ago
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // 30 days ago
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // We'll accumulate categories in this array
    const sections = [
      { label: 'Today', topics: [] },
      { label: 'Yesterday', topics: [] },
      { label: 'Previous 7 days', topics: [] },
      { label: 'Previous 30 days', topics: [] },
      // We'll dynamically add older months below
    ];

    // We'll keep a map from "May 2023" to { label: ..., topics: ... }
    const monthMap = {};

    sorted.forEach((topic) => {
      const lastModDate = toDate(topic.lastModified || 0);

      // Try "Today"
      if (lastModDate >= startOfToday) {
        sections[0].topics.push(topic);
        return;
      }

      // Try "Yesterday" (between startOfYesterday and startOfToday)
      if (lastModDate >= startOfYesterday && lastModDate < startOfToday) {
        sections[1].topics.push(topic);
        return;
      }

      // Try "Previous 7 days" (between sevenDaysAgo and startOfYesterday)
      if (lastModDate >= sevenDaysAgo) {
        sections[2].topics.push(topic);
        return;
      }

      // Try "Previous 30 days" (between thirtyDaysAgo and sevenDaysAgo)
      if (lastModDate >= thirtyDaysAgo) {
        sections[3].topics.push(topic);
        return;
      }

      // Otherwise, it's older than 30 days -> group by month (descending)
      const year = lastModDate.getFullYear();
      const month = lastModDate.getMonth(); // 0=Jan, 1=Feb...
      const monthLabel = new Intl.DateTimeFormat('en', { month: 'long' }).format(lastModDate) + ` ${year}`;
      if (!monthMap[monthLabel]) {
        monthMap[monthLabel] = { label: monthLabel, topics: [] };
      }
      monthMap[monthLabel].topics.push(topic);
    });

    // Convert monthMap to an array sorted descending by date
    // e.g., [ "July 2023", "June 2023", "May 2023", ... ]
    const monthSections = Object.values(monthMap).sort((a, b) => {
      // parse out the year from the label, e.g. 'May 2023'
      // then compare year and month descending
      const [monthA, yearA] = a.label.split(' ');
      const [monthB, yearB] = b.label.split(' ');
      
      // numeric months from names (rough approach)
      const dateA = new Date(parseInt(yearA), monthNameToIndex(monthA));
      const dateB = new Date(parseInt(yearB), monthNameToIndex(monthB));

      // Descending
      return dateB - dateA;
    });

    // Combine sections
    return [...sections, ...monthSections];
  };

  /**
   * Helper to convert month name (e.g. "January") to 0-based index.
   */
  const monthNameToIndex = (monthName) => {
    // e.g. "January" -> 0, "February" -> 1, ...
    // using an English list
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June', 
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames.indexOf(monthName);
  };

  const reloadTopics = async (title, mergedTopics = null, newTopic = null) => {
    // If a new topic is provided, add it to the topics list
    if (newTopic) {
      setTopics(prevTopics => [...prevTopics, newTopic]);
      return;
    }

    // If merged topics are provided, update the entire topics list
    if (mergedTopics) {
      setTopics(mergedTopics);
      
      // Find and select the current topic in the new list
      const currentTopic = mergedTopics.find(t => t.researchID === userData.researchID);
      if (currentTopic) {
        setSelectedTopic(currentTopic);
      }
      return;
    }

    if (!selectedTopic) return;
  
    // Original logic for updating a single topic
    try {
      // Get the latest data from the server
      const loadedTopics = await api.getPreviousResearches(sub);
      const responsesList = await api.getPreviousResponseData(sub);
      const personasList = await api.getAllPreviousPersonas(sub);
      
      // Find the updated topic with the matching researchID
      let updatedTopic = loadedTopics.find(t => t.researchID === selectedTopic.researchID);
      
      if (updatedTopic) {
        // Merge with response data
        const matchedResponses = responsesList.find(item => item.researchID === updatedTopic.researchID);
        const surveyResponses = mapServerResponsesToLocal(
          matchedResponses?.responses || null,
          matchedResponses?.researchID || null
        );
        
        // Merge with personas data
        const personasData = personasList.find(item => item.researchID === updatedTopic.researchID);
        const selectedPersonas = personasData?.personas || [];
        const personaFilters = personasData?.filters || [];
        
        // Create a complete updated topic with all the data
        updatedTopic = {
          ...updatedTopic,
          surveyResponses,
          selectedPersonas,
          personaFilters,
          lastModified: Math.floor(Date.now() / 1000)
        };
        
        if (title) updatedTopic.title = title;
        
        // Update the selectedTopic state
        setSelectedTopic(updatedTopic);
        
        // Update userData with the latest information
        const surveyQuestions = updatedTopic.surveyStatus === 'COMPLETED' 
          ? mapServerQuestionsToLocal(updatedTopic.survey?.questions || null, updatedTopic.survey?.order || null) || [] 
          : null;
        
        setUserData(prev => ({
          ...prev,
          researchID: updatedTopic.researchID,
          title: updatedTopic.title || '',
          description: updatedTopic.description || '',
          surveyQuestions,
          surveyDescription: updatedTopic.survey?.description || '',
          selectedPersonas: updatedTopic.selectedPersonas || [],
          personaFilters: updatedTopic.personaFilters || [],
          surveyResponses: updatedTopic.surveyResponses,
          surveyStatus: updatedTopic.surveyStatus,
          responsesStatus: updatedTopic.responsesStatus,
          personasStatus: updatedTopic.personasStatus,
          surveyContext: updatedTopic.survey?.survey_context || ''
        }));
        
        // Update the topics list with the updated topic
        setTopics(prevTopics => {
          const filtered = prevTopics.filter(t => t.researchID !== updatedTopic.researchID);
          return [...filtered, updatedTopic];
        });
        
        console.log("Topic data reloaded successfully:", updatedTopic);
      }
    } catch (error) {
      // console.error('Error reloading topic data:', error);
    }
  };

  // Function to check if surveys or personas have changed
  const checkForChanges = () => {
    // If we don't have a previously saved state, there's nothing to compare against
    if (!userData.lastGenerationState) return false;
    
    const lastState = userData.lastGenerationState;
    
    // Check if number of questions has changed
    if (lastState.questionCount !== userData.surveyQuestions?.length) return true;
    
    // Check if number of personas has changed
    if (lastState.personaCount !== Object.keys(userData.selectedPersonas || {}).length) return true;
    
    // Check if question content has changed
    if (lastState.questions) {
      for (let i = 0; i < userData.surveyQuestions?.length; i++) {
        const currentQ = userData.surveyQuestions[i];
        const lastQ = lastState.questions.find(q => q.questionID === currentQ.questionID);
        
        if (!lastQ) return true; // New question
        if (lastQ.questionText !== currentQ.questionText) return true; // Question text changed
        if (lastQ.questionType !== currentQ.questionType) return true; // Question type changed
        
        // For multiple-choice questions, check if options changed
        if (currentQ.questionType === 'select' || currentQ.questionType === 'multiselect') {
          if (!arraysEqual(lastQ.responseOptions, currentQ.responseOptions)) return true;
        }
      }
    }
    
    return false;
  };

  // Helper function to compare arrays
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  // Initial load
  useEffect(() => {
    if (sub) {
      Promise.all([
        api.getPreviousResearches(sub),
        api.getPreviousResponseData(sub),
        api.getAllPreviousPersonas(sub),
      ])
      .then(([loadedTopics, responsesList, personasList]) => {
        const mergedTopics = loadedTopics.map((topic) => {
          const match = responsesList.find(item => item.researchID === topic.researchID);
          const surveyResponses = mapServerResponsesToLocal(
            match?.responses || null,
            match?.researchID || null
          );

          const personasData = personasList.find(item => item.researchID === topic.researchID);
          const selectedPersonas = personasData?.personas || [];
          const personaFilters = personasData?.filters || [];          

          return { 
            ...topic, 
            surveyResponses, 
            selectedPersonas, 
            personaFilters
          };
        });
        setTopics(mergedTopics);
        setIsLoading(false); // Set loading to false after data is loaded
      })
      .catch(error => {
        // console.error('Error loading research data or responses:', error);
        setIsLoading(false); // Set loading to false even if there's an error
      });
    }
  }, [sub]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Topic handlers
  const handleNewResearchClick = () => {
    setSelectedTopic({ 
      userID: sub,
      researchID: '',
      description: '',
      title: '',
      surveyDescription: '',
      surveyQuestions: null,
      selectedPersonas: [],
      surveyResponses: null
    });
    setTabIndex(0);

    setUserData({
      ...userData,
      researchID: '',
      description: '',
      title: '',
      surveyDescription: '',
      surveyQuestions: null,
      selectedPersonas: [],
      personaFilters: null,
      surveyResponses: null,
      surveyContext: ''
    });
  };

  const handleTopicSelect = async (topic) => {
    console.log('🔍 handleTopicSelect called with topic:', topic);
    
    try {
      // If we already have a researchID, try to get the latest data from the server
      if (topic.researchID) {
        const loadedTopics = await api.getPreviousResearches(sub);
        const serverTopic = loadedTopics.find(t => t.researchID === topic.researchID);
        
        if (serverTopic) {
          // We found the topic on the server, use the server data
          topic = {
            ...topic,
            ...serverTopic
          };
        }
      }
      
      // If this topic has undefined surveyStatus, try to find a complete version in topics array
      let actualTopic = topic;
      if (!topic.surveyStatus && topic.title) {
        const completeVersion = topics.find(t => 
          t.title === topic.title && 
          t.surveyStatus === 'COMPLETED' && 
          t.researchID
        );
        if (completeVersion) {
          console.log('🔍 Found complete version, using that instead');
          actualTopic = completeVersion;
        }
      }
      
      const surveyCompleted = actualTopic.surveyStatus === 'COMPLETED';
      const surveyQuestions = surveyCompleted 
        ? mapServerQuestionsToLocal(actualTopic.survey?.questions || null, actualTopic.survey?.order || null) || [] 
        : null;
        
      console.log('🔍 surveyCompleted:', surveyCompleted);
      console.log('🔍 surveyQuestions:', surveyQuestions);
      
      setSelectedTopic(actualTopic);
      setUserData({
        userID: sub,
        researchID: actualTopic.researchID,
        description: actualTopic.description || '',
        title: actualTopic.title || '',
        surveyQuestions,
        surveyDescription: actualTopic.survey?.description || '',
        selectedPersonas: actualTopic.selectedPersonas || [],
        personaFilters: actualTopic.personaFilters || [],
        surveyResponses: actualTopic.surveyResponses,
        surveyStatus: actualTopic.surveyStatus,
        responsesStatus: actualTopic.responsesStatus,
        personasStatus: actualTopic.personasStatus,
        surveyContext: actualTopic.survey?.survey_context || ''
      });
    
      setTabIndex(0); // Show the first tab when selecting a topic
      console.log("DONE WITH HANDLETOPICSELECT, SELECTED TOPIC IS", actualTopic);
    } catch (error) {
      // console.error('Error loading topic details:', error);
      // Fallback to using the topic as-is if there's an error
      setSelectedTopic(topic);
      setUserData({
        userID: sub,
        researchID: topic.researchID,
        description: topic.description || '',
        title: topic.title || '',
        surveyQuestions: mapServerQuestionsToLocal(topic.survey?.questions || null, topic.survey?.order || null) || null,
        surveyDescription: topic.survey?.description || '',
        selectedPersonas: topic.selectedPersonas || [],
        personaFilters: topic.personaFilters || [],
        surveyResponses: topic.surveyResponses,
        surveyStatus: topic.surveyStatus,
        responsesStatus: topic.responsesStatus,
        personasStatus: topic.personasStatus,
        surveyContext: topic.survey?.survey_context || ''
      });
      setTabIndex(0);
    }
  };
  
  const handleDeleteClick = (researchID) => {
    setTopicToDelete(researchID);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (topicToDelete) {
      try {
        await api.deleteResearch(sub, topicToDelete);
        const updatedTopics = topics.filter(t => t.researchID !== topicToDelete);
        setTopics(updatedTopics);
        if (selectedTopic?.researchID === topicToDelete) {
          setSelectedTopic(null);
        }
      } catch (error) {
        // console.error('Error deleting research:', error);
      }
    }
    setDeleteDialogOpen(false);
    setTopicToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTopicToDelete(null);
  };

  const handleTopicDelete = async (researchID) => {
    if (!topicPendingDeletion) {
      setTopicPendingDeletion(researchID);
      // Clear in 5 seconds if not confirmed
      setTimeout(() => {
        setTopicPendingDeletion(null);
      }, 5000);
      return;
    }

    if (topicPendingDeletion === researchID) {
      try {
        await api.deleteResearch(sub, researchID);
        const updatedTopics = topics.filter(t => t.researchID !== researchID);
        setTopics(updatedTopics);
        if (selectedTopic?.researchID === researchID) {
          setSelectedTopic(null);
        }
        setTopicPendingDeletion(null);
      } catch (error) {
        // console.error('Error deleting research:', error);
      }
    }
  };

  const handleDuplicateResearch = async () => {
    console.log("IN HANDLE DUPLICATE RESEARCH, userData is", userData);
    console.log("IN HANDLE DUPLICATE RESEARCH, selectedTopic is", selectedTopic);

    if (!selectedTopic) return;
    
    try {
      setIsLoading(true);
      const result = await api.duplicateResearch(sub, userData.researchID);

      console.log("result of duplicate research is",result);

    // // Create a new topic based on selectedTopic
    // const duplicatedTopic = {
    //   ...re,
    //   researchID: result.researchID, // Use the new researchID from the result
    //   lastModified: result.lastModified // Use the new lastModified timestamp from the result
    // };

    // console.log("IN HANDLE DUPLICATE RESEARCH, new topic is",duplicatedTopic);


    
      // Add the duplicated topic to the topics list
      setTopics(prevTopics => [...prevTopics, result]);
      
      handleTopicSelect(result);
      
      // Show success message (you could add a snackbar/toast for this)
      console.log('Research duplicated successfully!');
    } catch (error) {
      // console.error('Error duplicating research:', error);
      // You could show an error message to the user here
    } finally {
      setIsLoading(false);
      console.log("DONE WITH DUPLICATING, SELECTED TOPIC IS NOW", selectedTopic);
    }
  };

  /**
   * Renders the entire list of topics by section.
   */
  const TopicList = ({ topics, onTopicClick, onTopicDelete }) => {
    // Group into labeled sections
    const sections = groupTopicsBySections(topics);

    return (
      <div className="topic-list">
        {isLoading ? (
          <div className="textbox container">Loading topics...</div>
        ) : topics.length === 0 ? (
          <div className="textbox container">No topics available.</div>
        ) : (
          sections.map((section) => {
            // If a section has no topics, skip it
            if (section.topics.length === 0) return null;

            return (
              <div key={section.label} style={{ marginBottom: '1rem' }}>
                <h3 style={{ margin: '0.5rem 0', color: 'var(--background-color)' }}>{section.label}</h3>
                <ul>
                  {section.topics.map((topic) => (
                    <li
                      key={topic.researchID}
                      onClick={() => onTopicClick(topic)}
                       className={`
                         ${topic.isPremium ? 'premium-topic' : 'container'}
                         ${topicPendingDeletion === topic.researchID ? 'pending-deletion' : ''}
                         ${selectedTopic?.researchID === topic.researchID ? 'selected-topic' : ''}
                       `}
                    >
                      <span>{topic.title}</span>
                      <div className="topic-actions">
                        {topic.isPremium && <span className="premium-star">★</span>}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '5px',
                            backgroundColor: 'var(--tertiary-color)'
                          }}
                        >
                          <DeleteIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(topic.researchID);
                            }}
                            sx={{
                              cursor: 'pointer',
                              color: 'var(--background-color)',
                              '&:hover': { color: 'black' }
                            }}
                          />
                        </Box>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <div className="research-page">
        <aside className={`sidebar ${isSidebarVisible ? 'visible' : 'collapsed'}`}>
          <div className="sidebar-header">
            <h2>Your Research Topics</h2>
          </div>
          <button 
            className="button standout-button research-button" 
            style={{ maxWidth: 'none', width: '90%' }} 
            onClick={handleNewResearchClick}
          >
            New Research
          </button>

          <TopicList
            topics={topics}
            onTopicClick={handleTopicSelect}
            onTopicDelete={handleTopicDelete}
            selectedTopic={selectedTopic}
          />
        </aside>
    
        <button 
          className="toggle-sidebar"
          onClick={() => setIsSidebarVisible(!isSidebarVisible)}
        >
          {isSidebarVisible ? '←' : '→'}
        </button>

        <main className={`research-content ${isSidebarVisible ? 'visible' : 'collapsed'}`}>
          {selectedTopic ? (
            <Box sx={{ p: 3, marginLeft: '2rem', marginRight: '2rem' }}>
              <Typography variant="h4" gutterBottom align="center">
                {selectedTopic.title}
              </Typography>
              {(userData.surveyStatus === 'COMPLETED' || userData.surveyStatus === 'EDITING' || 
                  (selectedTopic && selectedTopic.researchID && selectedTopic.description)) && (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      mb: 1
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Button
                          onClick={() => setShowDescription(!showDescription)}
                          startIcon={showDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          size="small"
                        >
                          {showDescription ? 'Hide Description' : 'Show Description'}
                        </Button>
                      </Box>
                    </Box>
                    <Collapse in={showDescription}>
                      <ResearchDescription description={selectedTopic.description || userData.description} />
                    </Collapse>
                  </Box>
                )}
              <Box sx={{ width: '100%', marginBottom: 3 }}>
                <Tabs 
                  value={tabIndex}
                  onChange={handleTabChange}
                  centered
                  

                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'var(--tertiary-color)',
                      textColor: "var(--tertiary-color)",
                    },
                    '& .Mui-selected': {
                      color: "var(--tertiary-color)",
                    },
                  }}
                  
                >
                  <Tab label="Survey" />
                  <Tab label="Personas" disabled={userData.surveyStatus !== 'COMPLETED'} />
                  <Tab label="Responses" disabled={userData.surveyStatus !== 'COMPLETED' || userData.personasStatus !== 'COMPLETED'} />
                  <Tab label="Final Review" disabled={
                    userData.surveyStatus !== 'COMPLETED' || 
                    userData.personasStatus !== 'COMPLETED' || 
                    userData.responsesStatus !== 'COMPLETED'
                  } />
                </Tabs>

                {/* Only show research description after survey has been generated or for existing topics */}
                

                {/* Tab content */}
                <Box sx={{ marginTop: 3, marginBottom: 10 }}>
                  {tabIndex === 0 && (
                    <Step1Survey
                      userData={userData}
                      setUserData={setUserData}
                      onTopicsChange={reloadTopics}
                      setTabIndex={setTabIndex}
                      onDuplicateResearch={handleDuplicateResearch}
                    />
                  )}
                  {tabIndex === 1 && (
                    <Step2Personas
                      userData={userData}
                      setUserData={setUserData}
                      onTopicsChange={reloadTopics}
                      setTabIndex={setTabIndex}
                    />
                  )}
                  {tabIndex === 2 && (
                    <Step3Responses
                      userData={userData}
                      setUserData={setUserData}
                      onTopicsChange={reloadTopics}
                      setTabIndex={setTabIndex}
                    />
                  )}
                  {tabIndex === 3 && (
                    <Step4FinalReview
                      userData={userData}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: '100%', marginBottom: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <Typography variant="h5" align="center">
                Select a research topic or create a new one to begin
              </Typography>
            </Box>
          )}
        </main>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: '10px'
            }
          }}
        >
          <DialogTitle id="delete-dialog-title">
            Confirm Deletion
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this research topic? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="var(--tertiary-color)">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              sx={{
                backgroundColor: '#D2042D',
                '&:hover': { backgroundColor: 'darkred' }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
}

export default ResearchPage;
